import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL, API_CONFIG } from '../config/api';
import socketService from '../services/socketService';

export const useNotifications = (user) => {
  const [messageCount, setMessageCount] = useState(0);
  const [supportTicketCount, setSupportTicketCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch unread message count
  const fetchMessageCount = useCallback(async () => {
    if (!user?.user_id) return;
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/messages/unread/${user.user_id}`, 
        API_CONFIG
      );
      const count = response.data.total_unread || 0;
      setMessageCount(count);
      return count;
    } catch (error) {
      console.error('Error fetching message count:', error);
      setError('Failed to fetch message notifications');
      return 0;
    }
  }, [user?.user_id]);

  // Fetch unread support ticket count
  const fetchSupportTicketCount = useCallback(async () => {
    if (!user?.user_id) return;

    try {
      let unreadCount = 0;

      // Different logic based on user role
      if (user.role_name === 'field_officer' || user.role_name === 'revenue_officer') {
        // Field officers: count tickets they created that have responses or status updates
        const response = await axios.get(
          `${API_BASE_URL}/support-tickets?created_by=${user.user_id}`,
          API_CONFIG
        );
        const tickets = response.data || [];

        // Count tickets that are not closed and might have updates
        unreadCount = tickets.filter(ticket =>
          ticket.status !== 'closed' && ticket.status !== 'resolved'
        ).length;

      } else if (user.role_name === 'mmda_admin' || user.role_name === 'mmda') {
        // MMDA admins: count tickets assigned to them (excluding closed/resolved)
        const response = await axios.get(
          `${API_BASE_URL}/support-tickets?assigned_to=${user.user_id}`,
          API_CONFIG
        );
        const tickets = response.data || [];
        // Filter out closed and resolved tickets
        unreadCount = tickets.filter(ticket =>
          ticket.status !== 'closed' && ticket.status !== 'resolved'
        ).length;

      } else if (user.role_name === 'super_admin') {
        // SuperAdmins: count tickets assigned to them from MMDA admins
        const response = await axios.get(
          `${API_BASE_URL}/support-tickets?assigned_to=${user.user_id}&status=open,in_progress`,
          API_CONFIG
        );
        const tickets = response.data || [];
        unreadCount = tickets.length;
      }

      setSupportTicketCount(unreadCount);
      return unreadCount;
    } catch (error) {
      console.error('Error fetching support ticket count:', error);
      setError('Failed to fetch support ticket notifications');
      return 0;
    }
  }, [user?.user_id, user?.role_name]);

  // Fetch all notification counts
  const fetchAllCounts = useCallback(async () => {
    if (!user?.user_id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [msgCount, ticketCount] = await Promise.all([
        fetchMessageCount(),
        fetchSupportTicketCount()
      ]);
      
      const total = msgCount + ticketCount;
      setTotalCount(total);
    } catch (error) {
      console.error('Error fetching notification counts:', error);
      setError('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, [fetchMessageCount, fetchSupportTicketCount, user?.user_id]);

  // Update total count when individual counts change
  useEffect(() => {
    setTotalCount(messageCount + supportTicketCount);
  }, [messageCount, supportTicketCount]);

  // Set up socket listeners for real-time updates
  useEffect(() => {
    if (!user?.user_id) return;

    // Listen for new messages
    const handleNewMessage = (message, isSent = false) => {
      if (!isSent && message.receiver_id === user.user_id) {
        // Increment message count for received messages
        setMessageCount(prev => prev + 1);
      }
    };

    // Listen for support ticket updates
    const handleTicketCreated = (ticketData) => {
      // Refresh count if this user is involved
      if (ticketData.assigned_to === user.user_id || ticketData.created_by === user.user_id) {
        fetchSupportTicketCount();
      }
    };

    const handleTicketComment = (commentData) => {
      // Refresh count if this user is involved with the ticket
      if (commentData.ticket &&
          (commentData.ticket.assigned_to === user.user_id ||
           commentData.ticket.created_by === user.user_id)) {
        fetchSupportTicketCount();
      }
    };

    // Add socket listeners
    const removeMessageListener = socketService.addMessageListener(handleNewMessage);

    // Add support ticket event listeners
    const socket = socketService.getSocket();
    if (socket) {
      socket.on('support_ticket_created', handleTicketCreated);
      socket.on('support_ticket_comment', handleTicketComment);
    }

    return () => {
      removeMessageListener();
      if (socket) {
        socket.off('support_ticket_created', handleTicketCreated);
        socket.off('support_ticket_comment', handleTicketComment);
      }
    };
  }, [user?.user_id, fetchSupportTicketCount]);

  // Initial fetch and periodic refresh
  useEffect(() => {
    if (user?.user_id) {
      fetchAllCounts();
      
      // Refresh counts every 2 minutes
      const interval = setInterval(fetchAllCounts, 120000);
      return () => clearInterval(interval);
    }
  }, [user?.user_id, fetchAllCounts]);

  // Mark messages as read (reduces message count)
  const markMessagesAsRead = useCallback(async (senderId) => {
    try {
      await axios.put(`${API_BASE_URL}/messages/read`, {
        sender_id: senderId,
        receiver_id: user.user_id
      }, API_CONFIG);
      
      // Refresh message count
      await fetchMessageCount();
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  }, [user?.user_id, fetchMessageCount]);

  // Mark support ticket as read/viewed
  const markTicketAsViewed = useCallback(async (ticketId) => {
    try {
      // This would require backend support to track viewed status
      // For now, just refresh the count
      await fetchSupportTicketCount();
    } catch (error) {
      console.error('Error marking ticket as viewed:', error);
    }
  }, [fetchSupportTicketCount]);

  // Refresh all counts manually
  const refreshCounts = useCallback(() => {
    fetchAllCounts();
  }, [fetchAllCounts]);

  return {
    messageCount,
    supportTicketCount,
    totalCount,
    loading,
    error,
    fetchAllCounts,
    fetchMessageCount,
    fetchSupportTicketCount,
    markMessagesAsRead,
    markTicketAsViewed,
    refreshCounts
  };
};
